let pending2FAState = null;
let pending2FATimeoutId = null;
let is2FAInProgress = false;
let beforeUnloadListener = null;
let pending2FARetrieved = false;

const PENDING_2FA_TTL_MS = 90 * 1000;

/**
 * Stores 2FA pending data with password encapsulated in a closure
 * @param {Object} data - Object containing username, password, challenge_id, and other metadata
 * @throws {Error} If a 2FA flow is already in progress
 * @throws {Error} If data.username or data.password are missing or empty
 */
export function setPending2FA(data) {
    if (!data || typeof data !== 'object') {
        throw new Error('setPending2FA: data must be a non-null object');
    }

    if (!data.username || typeof data.username !== 'string' || data.username.trim() === '') {
        throw new Error('setPending2FA: data.username is required and must be a non-empty string');
    }

    if (!data.password || typeof data.password !== 'string' || data.password.trim() === '') {
        throw new Error('setPending2FA: data.password is required and must be a non-empty string');
    }

    if (!data.type || typeof data.type !== 'string') {
        throw new Error('setPending2FA: data.type is required and must be a string');
    }

    if (is2FAInProgress) {
        throw new Error('2FA flow already in progress');
    }

    clearPending2FA();

    is2FAInProgress = true;

    const passwordString = data.password;
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(passwordString);

    let passwordAccessed = false;

    const getPassword = function() {
        if (passwordAccessed) {
            throw new Error('Password access function can only be called once');
        }
        if (!pending2FAState) {
            throw new Error('2FA state has been cleared');
        }
        passwordAccessed = true;
        const decoder = new TextDecoder('utf-8');
        return decoder.decode(passwordBuffer);
    };

    // Function to securely clear password buffer
    const clearPassword = function() {
        if (passwordBuffer) {
            passwordBuffer.fill(0);
        }
    };

    pending2FAState = {
        username: data.username,
        challenge_id: data.challenge_id || null,
        type: data.type,
        isIntegration: data.isIntegration || false,
        redirect_url: data.redirect_url || null,
        client_id: data.client_id || null,
        hash: data.hash || null,
        access_code_flag: data.access_code_flag || false,
        createdAt: Date.now(),
        getPassword: getPassword,
        clearPassword: clearPassword
    };

    pending2FATimeoutId = setTimeout(function() {
        clearPending2FA();
    }, PENDING_2FA_TTL_MS);

    if (typeof window !== 'undefined' && !beforeUnloadListener) {
        beforeUnloadListener = function() {
            clearPending2FA();
        };
        window.addEventListener('beforeunload', beforeUnloadListener);
    }
}

/**
 * Gets pending 2FA data if still valid (not expired)
 *
 * SECURITY: This function is destructive - it can only be called once per state.
 * Subsequent calls will return null to prevent multiple consumers from accessing
 * the getPassword capability, ensuring true single-use access to the password.
 * This function can be called only once per state, but single-access password protection is enforced by the returned
 * getPassword() function.
 *
 * @returns {Object|null} - Pending 2FA data with getPassword capability or null if expired/not found/already retrieved
 */
export function getPending2FA() {
    if (!pending2FAState) {
        return null;
    }

    if (pending2FARetrieved) {
        return null;
    }

    const now = Date.now();
    const age = now - pending2FAState.createdAt;
    if (age >= PENDING_2FA_TTL_MS) {
        clearPending2FA();
        return null;
    }

    pending2FARetrieved = true;

    return {
        username: pending2FAState.username,
        challenge_id: pending2FAState.challenge_id,
        type: pending2FAState.type,
        isIntegration: pending2FAState.isIntegration,
        redirect_url: pending2FAState.redirect_url,
        client_id: pending2FAState.client_id,
        hash: pending2FAState.hash,
        access_code_flag: pending2FAState.access_code_flag,
        getPassword: pending2FAState.getPassword,
        clearPassword: pending2FAState.clearPassword
    };
}

/**
 * Clears pending 2FA data from memory
 * Actively overwrites the password buffer with zeros before nullifying state
 * to ensure the secret is erased from memory. Also clears the timeout and
 * removes the beforeunload listener to prevent leaks.
 */
export function clearPending2FA() {
    if (pending2FAState) {
        if (pending2FAState.clearPassword) {
            pending2FAState.clearPassword();
        }

        if (pending2FAState.getPassword) {
            pending2FAState.getPassword = null;
        }

        pending2FAState = null;
    }

    if (pending2FATimeoutId) {
        clearTimeout(pending2FATimeoutId);
        pending2FATimeoutId = null;
    }

    if (typeof window !== 'undefined' && beforeUnloadListener) {
        window.removeEventListener('beforeunload', beforeUnloadListener);
        beforeUnloadListener = null;
    }

    is2FAInProgress = false;
    pending2FARetrieved = false;
}

/**
 * Checks if a 2FA flow is currently in progress
 * @returns {boolean}
 */
export function is2FAFlowActive() {
    return is2FAInProgress && pending2FAState !== null;
}

/**
 * Checks if pending 2FA data exists without consuming it (non-destructive check)
 * Useful for checking if data is available before calling getPending2FA()
 * @returns {boolean} - true if pending 2FA data exists and is not expired, false otherwise
 */
export function hasPending2FA() {
    if (!pending2FAState) {
        return false;
    }

    const now = Date.now();
    const age = now - pending2FAState.createdAt;
    if (age >= PENDING_2FA_TTL_MS) {
        clearPending2FA();
        return false;
    }

    return true;
}